import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, map, Observable, of, tap } from 'rxjs';
import { DynamicFormComponent } from 'src/app/components/dynamic-form/dynamic-form.component';
import { IHasUnsavedChanges } from 'src/app/guard/unsaved-changes.guard';
import { IconModule } from 'src/app/icon/icon.module';
import { MaterialModule } from 'src/app/material.module';
import { CompanyService } from 'src/app/services/company.service';
import { CountryService } from 'src/app/services/country.service';
import { ToastService } from 'src/app/services/toast.service';
import { PATTERN, ROUTES } from 'src/app/shared/constants';
import { FormControlType, SCREEN_SIZE } from 'src/app/shared/enums';
import { IFormConfig } from 'src/app/shared/interfaces/IForm';
import { hasFormChanges } from 'src/app/shared/utils';

@Component({
  selector: 'app-company-detail',
  imports: [DynamicFormComponent, MaterialModule, IconModule],
  templateUrl: './company-detail.component.html',
  styleUrls: ['./company-detail.component.scss']
})
export class CompanyDetailComponent implements OnInit, IHasUnsavedChanges {
  @ViewChild('companyDetailForm', { static: true }) companyDetailForm!: DynamicFormComponent;
  companyForm: FormGroup;
  formConfigs: IFormConfig;
  spinLoader = false;
  isEdit = true;
  firstCharacterCompany = "";
  company: any;
  readonly unsavedChangesMessageKey = 'company information unsaved changes message';
  private initialFormValue: unknown;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private countryService: CountryService,
    private companyService: CompanyService) {}

  ngOnInit() {
    this.initForm();
    this.getCompany();
  }

  hasUnsavedChanges(): boolean {
    return hasFormChanges(this.companyForm.getRawValue(), this.initialFormValue);
  }

  private captureFormSnapshot(): void {
    this.initialFormValue = structuredClone(this.companyForm.getRawValue());
  }

  get isAdmin(): boolean {
    return this.router.url.startsWith('/admin');
  }

  close = () => {
    this.router.navigate([ROUTES.ADMIN.COMPANIES]);
  }

  getCompany() {
    const companyId = this.route.snapshot.params['companyId'];
    if (!companyId) {
      this.captureFormSnapshot();
      return;
    }
    this.companyService.getCompany(companyId).subscribe((res) => {
      this.company = res;
      this.companyForm.reset(res);
      this.firstCharacterCompany = this.getInitials(res.name);
      this.captureFormSnapshot();
    });
  }

  initForm() {
    const appearance = this.isAdmin ? 'outline' : 'fill';

    this.companyForm = this.formBuilder.group({
      id: [null],
      name: [{ value: null }, [Validators.required, Validators.maxLength(50)]],
      email: [{ value: null }, [Validators.required, Validators.maxLength(100), Validators.pattern(PATTERN.EMAIL)]],
      phone: [{ value: null }, [Validators.maxLength(20), Validators.pattern(PATTERN.PHONE)]],
      address: [{ value: null }, [Validators.maxLength(50)]],
      zipCode: [{ value: null }, [Validators.maxLength(20), Validators.pattern(PATTERN.ZIP_GENERIC)]],
      city: [{ value: null }, [Validators.maxLength(50)]],
      countryId: [{ value: null }],
      isActive: [false]
    });

    this.formConfigs = {
      id: 'companyForm',
      preventEnterSubmit: true,
      styles: {
        bodyClass: 'company-detail-form-body',
        footerClass: 'flex items-center justify-end border-t border-[#e0e2ec] p-4!',
        footerNotice: {
          text: 'unsaved changes',
          hidden: () => !this.hasUnsavedChanges()
        }
      },
      actions: [
        {
          text: 'save',
          type: 'submit',
          id: "button-submit-company",
          disabled: () => !this.isEdit,
          hidden: () => !this.isAdmin,
          class: 'h-[36px]! text-[12px]! cursor-pointer!'
        }
      ],
      items: [
        {
          key: 'name',
          label: 'name',
          layoutConfig: [{ column: 12 }],
          appearance,
          controlType: FormControlType.INPUT,
          inputConfig: { readonly: !this.isAdmin },
          validator: [
            { errorType: 'required', errorMessage: 'name required' },
            { errorType: 'maxlength', errorMessage: 'name is too long', params: { length: 50 }}
          ]
        },
        {
          key: 'email',
          label: 'email',
          layoutConfig: [{ column: 12 }],
          appearance,
          controlType: FormControlType.INPUT,
          inputConfig: { readonly: !this.isAdmin },
          validator: [
            { errorType: 'required', errorMessage: 'email required' },
            { errorType: 'pattern', errorMessage: 'email invalid' }
          ]
        },
        {
          key: 'address',
          label: 'address',
          layoutConfig: [{ column: 12 }, { column: 6, screenSize: SCREEN_SIZE.MEDIUM }],
          appearance,
          controlType: FormControlType.INPUT,
          inputConfig: { readonly: !this.isAdmin },
          validator: [{ errorType: 'maxlength', errorMessage: 'address is too long', params: { length: 50 }}]
        },
        {
          key: 'phone',
          label: 'phone',
          layoutConfig: [{ column: 12 }, { column: 6, screenSize: SCREEN_SIZE.MEDIUM }],
          appearance,
          controlType: FormControlType.INPUT,
          inputConfig: { readonly: !this.isAdmin },
          validator: [
            { errorType: 'pattern', errorMessage: 'phone invalid' },
          ]
        },
        {
          key: 'zipCode',
          label: 'zip code',
          layoutConfig: [{ column: 12 }, { column: 6, screenSize: SCREEN_SIZE.MEDIUM }],
          appearance,
          controlType: FormControlType.INPUT,
          inputConfig: {
            isNumber: true,
            type: 'number',
            readonly: !this.isAdmin
          },
          validator: [
            { errorType: 'pattern', errorMessage: 'zip code invalid' },
            { errorType: 'maxlength', errorMessage: 'zip code is too long', params: { length: 20 }}
          ]
        },
        {
          key: 'city',
          label: 'city',
          layoutConfig: [{ column: 12 }, { column: 6, screenSize: SCREEN_SIZE.MEDIUM }],
          appearance,
          controlType: FormControlType.INPUT,
          inputConfig: { readonly: !this.isAdmin },
          validator: [{ errorType: 'maxlength', errorMessage: 'city is too long', params: { length: 50 }}]
        },
        {
          key: 'countryId',
          label: 'country',
          layoutConfig: [{ column: 12 }, { column: 6, screenSize: SCREEN_SIZE.MEDIUM }],
          appearance,
          controlType: FormControlType.SELECT,
          fetchDataAPI: () => this.countryService.getCountries({ pageSize: 1000 }),
          inputConfig: { readonly: !this.isAdmin },
          selectionConfig: {
            title: 'name',
            key: 'id'
          },
        },
        {
          key: 'isActive',
          label: 'active',
          controlType: FormControlType.SWITCH,
          layoutConfig: [{ column: 12 }, { column: 2, screenSize: SCREEN_SIZE.MEDIUM }],
          itemClass: 'd-flex align-items-center ml-0!',
          hidden: () => !this.isAdmin
        }
      ]
    }
  }

  onSubmit = () => {
    this.saveUnsavedChanges().subscribe();
  }

  saveUnsavedChanges(): Observable<boolean> {
    if (!this.companyForm.valid) {
      this.companyForm.markAllAsTouched();
      return of(false);
    }
    this.spinLoader = true;
    const payload = this.companyForm.getRawValue();

    return this.companyService.addOrUpdateCompany(payload).pipe(
      tap((res) => {
        if (res) {
          this.companyService.companyInfo = null;
          this.toastService.success(this.translateService.instant('update successfully'));
          this.firstCharacterCompany = this.getInitials(payload.name);
          this.company = { ...this.company, ...payload };
          this.captureFormSnapshot();
        }
      }),
      map((res) => !!res),
      catchError(({ error }) => {
        this.companyDetailForm.ValidatorError = error;
        return of(false);
      }),
      finalize(() => this.spinLoader = false)
    );
  }

  getInitials(fullName: any) {
    if (!fullName) return '';

    const firstName = fullName.trim().split(/\s+/)[0];
    return firstName.charAt(0).toUpperCase();
  }
}
